import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DatasetProfileDto } from 'src/app/data-transfer/entities/doc-entities/dataset-profile-dto';
import { CustomFieldDto } from 'src/app/data-transfer/entities/custom-field-dto';
import { EvidenceTypeDto } from 'src/app/data-transfer/core-data/doc/evidence-type-dto';
import { DialogActions } from 'src/app/model/dictionary';

export class TdProfileDialogData {
  constructor(
    public readonly configKey: string,
    public readonly tabLabelKey: string,
    public readonly profile: DatasetProfileDto,
    public readonly isEditingNew: boolean,
    public readonly existingProfileNames: string[],
    public readonly evidenceTypes: EvidenceTypeDto[],
    public readonly availableCustomFields: CustomFieldDto[],
    public readonly minimumRequirements: string[],
    public readonly unitProfiles: DatasetProfileDto[] = [],
    public readonly componentProfiles: DatasetProfileDto[] = [],
    public readonly multiMaterialProfiles: DatasetProfileDto[] = []
  ) {}
}

type SelectionMode = 'minimum' | 'all' | 'none';

@Component({
  selector: 'app-td-profile-dialog',
  templateUrl: './td-profile-dialog.component.html',
  styleUrls: ['./td-profile-dialog.component.scss'],
})
export class TdProfileDialogComponent {
  readonly noProfileId = '';

  editingProfile: DatasetProfileDto;
  nameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TdProfileDialogData,
    private dialogRef: MatDialogRef<TdProfileDialogComponent>
  ) {
    this.editingProfile = structuredClone(data.profile);
    this.nameControl.setValue(this.editingProfile.profileName);
  }

  get treeNode(): Record<string, unknown> | null {
    const nodeData = this.editingProfile['data'];
    return nodeData && typeof nodeData === 'object' ? (nodeData as Record<string, unknown>) : (this.editingProfile as Record<string, unknown>);
  }

  get excludedEvidenceTypeIds(): string[] {
    return (this.treeNode?.['excludedEvidenceTypes'] as string[]) ?? [];
  }

  onEvidenceTypesChange(ids: string[]): void {
    const node = this.treeNode;
    if (!node) return;
    node['excludedEvidenceTypes'] = ids;
  }

  getReferenceId(key: string): string {
    return (this.editingProfile[key] as string | null) ?? this.noProfileId;
  }

  setReferenceId(key: string, value: string): void {
    this.editingProfile[key] = value === this.noProfileId ? null : value;
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private collectBooleanFields(node: Record<string, unknown>): { node: Record<string, unknown>; key: string }[] {
    const fields: { node: Record<string, unknown>; key: string }[] = [];
    Object.entries(node).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        fields.push({ node, key });
      } else if (this.isPlainObject(value)) {
        fields.push(...this.collectBooleanFields(value));
      }
    });
    return fields;
  }

  getSelectionMode(): SelectionMode | null {
    const node = this.treeNode;
    if (!node) return null;
    const fields = this.collectBooleanFields(node);
    if (fields.every(field => field.node[field.key] === true)) return 'all';
    if (fields.every(field => field.node[field.key] === false)) return 'none';
    const minimumRequirements = new Set(this.data.minimumRequirements);
    if (fields.every(field => (field.node[field.key] === true) === minimumRequirements.has(field.key))) return 'minimum';
    return null;
  }

  applySelectionMode(mode: SelectionMode): void {
    const node = this.treeNode;
    if (!node) return;
    const minimumRequirements = new Set(this.data.minimumRequirements);
    this.collectBooleanFields(node).forEach(({ node: fieldNode, key }) => {
      fieldNode[key] = mode === 'all' ? true : mode === 'none' ? false : minimumRequirements.has(key);
    });
  }

  save(): void {
    this.nameControl.markAsTouched();
    const trimmedName = this.nameControl.value.trim();
    if (!trimmedName) {
      this.nameControl.setErrors({ required: true });
      return;
    }
    if (this.data.existingProfileNames.includes(trimmedName)) {
      this.nameControl.setErrors({ duplicateName: true });
      return;
    }

    const candidate: DatasetProfileDto = { ...this.editingProfile, profileName: trimmedName };
    this.dialogRef.close({ event: DialogActions.CONFIRM, data: candidate });
  }

  closeDialog(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
